// forms.js - Supabase Forms Service

import axios from "axios";
import supabase from "../utils/supabase";

export const Forms = {
    /**
     * Submit contact form data to Supabase
     * @param {Object} formData - The contact form data
     * @param {string} formData.name - Full name
     * @param {string} formData.company - Company name (optional)
     * @param {string} formData.phone - Phone number (optional)
     * @param {string} formData.email - Email address
     * @param {string} formData.subject - Message subject
     * @param {string} formData.message - Message content
     * @returns {Promise<Object>} Response object with success/error status
     */
    async submitContactForm(formData) {
        try {
            const response = await axios.post('https://boldedgemedia-be-website.vercel.app/form/contact', formData, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            return {
                success: true,
                data: response.data,
                message: 'Form submitted successfully!',
            };
        } catch (error) {
            console.error('Axios form submit error:', error?.response?.data || error.message);
            return {
                success: false,
                error: error?.response?.data || error.message,
                message: 'Failed to submit form. Please try again.',
            };
        }
    },

    /**
     * Get all contact form submissions (for admin use)
     * @param {Object} options - Query options
     * @param {number} options.limit - Number of records to fetch
     * @param {number} options.offset - Number of records to skip
     * @param {string} options.status - Filter by status ('new', 'in_progress', 'resolved')
     * @returns {Promise<Object>} Response with forms data
     */
    async getContactForms(options = {}) {
        try {
            const { limit = 50, offset = 0, status } = options;

            let query = supabase
                .from('contact_forms')
                .select('*')
                .order('created_at', { ascending: false });

            if (status) {
                query = query.eq('status', status);
            }

            if (limit) {
                query = query.range(offset, offset + limit - 1);
            }

            const { data, error, count } = await query;

            if (error) {
                throw new Error(error.message);
            }

            return {
                success: true,
                data: data || [],
                count: count,
                message: 'Forms retrieved successfully'
            };

        } catch (error) {
            console.error('Error fetching forms:', error);
            return {
                success: false,
                error: error.message,
                message: 'Failed to fetch forms'
            };
        }
    },

    /**
     * Update contact form status
     * @param {string} id - Form ID
     * @param {string} status - New status ('new', 'in_progress', 'resolved')
     * @param {string} notes - Optional admin notes
     * @returns {Promise<Object>} Response object
     */
    async updateFormStatus(id, status, notes = null) {
        try {
            const updateData = {
                status: status,
                updated_at: new Date().toISOString()
            };

            if (notes) {
                updateData.admin_notes = notes;
            }

            const { data, error } = await supabase
                .from('contact_forms')
                .update(updateData)
                .eq('id', id)
                .select()
                .single();

            if (error) {
                throw new Error(error.message);
            }

            return {
                success: true,
                data: data,
                message: 'Form status updated successfully'
            };

        } catch (error) {
            console.error('Error updating form status:', error);
            return {
                success: false,
                error: error.message,
                message: 'Failed to update form status'
            };
        }
    }
};